import BadgeValidationModal from '../../../../components/BadgeValidationModal/BadgeValidationModal'
import ScanCode from '../RestockingWebAppForm/components/ScanCode/ScanCode'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import StartExecutionModalCheckinList from '../StartExecutionModalCheckinList/StartExecutionModalCheckinList'
import TableMui from 'src/components/TableMui'
import FormProductsList from '../FormProductsList'
import {
  CheckinGenerateRestockListError, RestockingListType, RestockingListTypesEnum, RestockingMovementTypesEnum,
} from '../../RestockingTypes'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import { selectProductsList } from '../../RestockingSelector'
import { FormGenerateLists } from '../FormGenerateLists/FormGenerateLists'
import { SelectChangeEvent, Tooltip } from '@mui/material'
import { fixInputSx, transferTypes } from '../../utils'
import { actions } from 'src/features/restocking/RestockingState'
import { useSelector as storeUseSelector } from 'src/utils/store'
import { useForm } from 'react-hook-form'
import { DeleteOutline } from '@mui/icons-material'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { selectFulfillmentCenters } from 'src/features/selector'
import * as S from './TableProductsList.styles'
import dayjs from 'dayjs'

interface FormData {
  badge: string;
  orderBoxBarcode: string;
}

interface TableProductsListProps {
  forTransfer: boolean;
}

export const TableProductsList = ({
  forTransfer,
}: TableProductsListProps) => {
  const productList = useSelector(selectProductsList)
  const { restockingListTypes, orderCodes, user } = storeUseSelector((state) => state.restocking)
  const [shedCode, setShedCode] = useState<string[]>([])
  const [restockingSubType, setRestockingSubType] = useState<string>('')
  const [restockingType, setRestockingType] = useState<number>(-1)
  const [selectedRestockingType, setSelectedRestockingType] = useState<RestockingListType>()
  const [restockingSourceMovement, setRestockingSourceMovement] = useState<number>(-1)
  const [restockingDestinationMovement, setRestockingDestinationMovement] = useState<number>(-1)
  const [showModalBadgeValidation, setShowModalBadgeValidation] = useState(false)
  const [badgeValidationSuccess, setBadgeValidationSuccess] = useState(false)
  const [showModalConfirmDeleteOrder, setShowModalConfirmDeleteOrder] = useState(false)
  const [showModalConfirmCreateList, setShowModalConfirmCreateList] = useState(false)
  const [showModalToStartExecution, setShowModalToStartExecution] = useState(false)
  const [orderCodeToDelete, setOrderCodeToDelete] = useState('')
  const [checkinListGenerationErrors, setCheckinListGenerationErrors] = useState<CheckinGenerateRestockListError[]>([])
  const [modalCheckinListGenerationError, setModalCheckinListGenerationError] = useState(false)
  const [destinationFc, setDestinationFc] = useState<number>(-1)
  const [fcDestinationError, setFcDestinationError] = useState<string>('')
  const [shippingDate, setShippingDate] = useState<string>(dayjs().format('YYYY-MM-DD'))

  const dispatch = useDispatch()

  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const {
    register,
    getValues,
    setError,
    trigger,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>()

  const maxSourceAddress = 6

  const restockingListTypesOptions = useMemo(() => restockingListTypes
    .filter((type) => (forTransfer ? transferTypes.includes(type.id) : true))
    .map((type) => (
      {
        id: type.id,
        description: type.description,
      }
    )), [forTransfer, restockingListTypes])

  const restockingMovementTypeOptions = Object.keys(RestockingMovementTypesEnum)
    .filter((key) => RestockingMovementTypesEnum[key] !== RestockingMovementTypesEnum.FREE)
    .map((key, index) => ({
      id: index,
      description: RestockingMovementTypesEnum[key],
    }))

  const { fc, validateFc } = useFulfillmentCenter()

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    dispatch(actions.getRestockingTypes())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setRestockingSourceMovement(-1)
    setRestockingDestinationMovement(-1)
    const restockingListType = restockingListTypes.find((type) => type.id === restockingType)
    if (restockingListType) {
      setSelectedRestockingType(restockingListType)
      if (restockingListType.source !== RestockingMovementTypesEnum.FREE) {
        const movementType = restockingMovementTypeOptions.find((type) => type.description === restockingListType.source)
        if (movementType) {
          setRestockingSourceMovement(movementType.id)
        }
      }
      if (restockingListType.destination !== RestockingMovementTypesEnum.FREE) {
        const movementType = restockingMovementTypeOptions.find((type) => type.description === restockingListType.destination)
        if (movementType) {
          setRestockingDestinationMovement(movementType.id)
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restockingType])

  const isValidFcDestination = (): boolean => {
    if (transferTypes.includes(restockingType) && destinationFc === -1) {
      setFcDestinationError('O FC de destino é obrigatório')
      return false
    }
    setFcDestinationError('')
    return true
  }

  const tableRows = productList?.products?.map((product) => {
    const addressArray: string[] = []
    const expirationDateArray: (Date | string)[] = []
    const quantityArray: (number | string)[] = []

    if (product.sourceAddresses && !product.noReserve) {
      const { sourceAddresses } = product
      if (restockingType === RestockingListTypesEnum.RESTOCKING_FRESH_AUTOMATIC) {
        sourceAddresses.forEach((item) => {
          addressArray.push(item.address)
          expirationDateArray.push(item.expirationDate)
          quantityArray.push(item.quantity)
        })
      } else {
        for (let i = 0; i < sourceAddresses.length && i !== maxSourceAddress; i += 1) {
          addressArray.push(sourceAddresses[i].address)
          expirationDateArray.push(sourceAddresses[i].expirationDate)
          quantityArray.push(sourceAddresses[i].quantity)
        }
      }
    } else {
      addressArray.push('-')
      expirationDateArray.push('-')
      quantityArray.push('Sem reserva')
    }

    const productDescription = (
      <p>{product.sourceAddresses?.length > addressArray.length && restockingType !== RestockingListTypesEnum.RESTOCKING_FRESH_AUTOMATIC
        ? (
          <Tooltip title='Produto com endereços de excedente ocultos' placement='top' arrow>
            <span>*{product.productDescription}*</span>
          </Tooltip>
        ) : <span>{product.productDescription}</span>}
      </p>
    )

    return {
      productCode: <p>{product.productCode}</p>,
      productDescription,
      restockQuantity: product.noCapacity ? <p style={{ color: 'red' }}>Capacidade não cadastrada</p> : <p>{product.restockQuantity}</p>,
      address: product.noCapacity ? <p>-</p> : addressArray.map((address) => <p>{address}</p>),
      quantity: product.noCapacity ? <p>-</p> : quantityArray.map((quantity) => <p>{quantity}</p>),
      expirationDate: product.noCapacity ? <p>-</p> : expirationDateArray.map((expirationDate) => {
        if (typeof expirationDate === 'string' && Date.parse(expirationDate)) {
          return <p>{formatDateReplaceHour(new Date(expirationDate))}</p>
        }
        return <p>{expirationDate}</p>
      }),
      minimumExpirationDate: product.noCapacity ? <p>-</p> : <p> {formatDateReplaceHour(product.minimumExpirationDate)}</p>,
      destinationAddress: <p>{product.destinationAddress || '-'}</p>,
    }
  })

  useEffect(() => {
    if (restockingType === RestockingListTypesEnum.CHECK_IN_GROCERY
      || restockingType === RestockingListTypesEnum.CHECK_IN_FRESH
    ) {
      setShowModalBadgeValidation(true)
    }
  }, [restockingType])

  const closeShowModalBadgeValidation = () => {
    clearErrors('badge')
    setShowModalBadgeValidation(false)
    setRestockingType(-1)
  }

  const closeModalConfirmDeleteOrder = () => setShowModalConfirmDeleteOrder(false)
  const closeModalConfirmCreateList = () => setShowModalConfirmCreateList(false)

  const onBadgeValidationSuccess = () => {
    setBadgeValidationSuccess(true)
    setShowModalBadgeValidation(false)
  }

  const onBadgeSubmit = async () => {
    const validationSuccess = await trigger('badge')

    if (validationSuccess) {
      dispatch(
        actions.validateBadgeForListCreation({
          badge: getValues('badge'),
          createList: true,
          setError: (message) => setError('badge', { message }),
          onSuccess: onBadgeValidationSuccess,
        }),
      )
    }
  }

  const deleteOrderCode = () => {
    dispatch(actions.deleteOrderCode(orderCodeToDelete))
    setShowModalConfirmDeleteOrder(false)
  }

  const isOrderAlreadyScanned = (orderCode: string) => {
    if (orderCodes.includes(orderCode)) {
      setError('orderBoxBarcode', { message: 'Pedido já adicionado' })
      return true
    }
    return false
  }

  const onOrderBoxBarcodeSubmit = async () => {
    if (!fc) return

    const orderBoxCode = getValues('orderBoxBarcode').trim()

    if (isOrderAlreadyScanned(orderBoxCode)) return

    if (!orderBoxCode) {
      setError('orderBoxBarcode', { message: 'Código da caixa do pedido é obrigatório' })
      return
    }

    dispatch(actions.restockingCheckinValidate({
      badge: user.badge,
      orderBoxCode,
      idRestockingType: restockingType,
    }))

    reset({ orderBoxBarcode: '' })
  }

  const generateCheckinRestockList = () => {
    const onSuccessGenerateCheckinRestockList = (errorList: CheckinGenerateRestockListError[]) => {
      setShowModalConfirmCreateList(false)
      setModalCheckinListGenerationError(false)
      setCheckinListGenerationErrors(errorList)
      setShowModalToStartExecution(true)
    }

    const onErrorGenerateCheckinRestockList = (errorList: CheckinGenerateRestockListError[]) => {
      setShowModalConfirmCreateList(false)
      setModalCheckinListGenerationError(true)
      setCheckinListGenerationErrors(errorList)
      setShowModalToStartExecution(true)
    }

    dispatch(
      actions.generateCheckinRestockList({
        badge: user.badge,
        idFulfillmentCenter: fc.id,
        idType: restockingType,
        ordersCode: orderCodes,
        onSuccess: onSuccessGenerateCheckinRestockList,
        onError: onErrorGenerateCheckinRestockList,
      }),
    )
  }

  const removeErroredOrdersAndCloseModal = () => {
    const erroredOrderCodes = checkinListGenerationErrors.map((error) => error.orderCode)
    const updatedOrderCodes = orderCodes.filter((code) => !erroredOrderCodes.includes(code))
    dispatch(actions.setProperty({ property: 'orderCodes', value: updatedOrderCodes }))
    setShowModalToStartExecution(false)
  }

  const handleChangeRestockingType = (e: SelectChangeEvent<unknown>) => {
    setRestockingType(e.target.value as number)
    if (badgeValidationSuccess) setBadgeValidationSuccess(false)
    dispatch(actions.clearProperty('orderCodes'))
    reset({ orderBoxBarcode: '' })
  }

  const orderCodeRows = orderCodes.map((orderCode) => ({
    orderCode,
    remove: (
      <DeleteOutline
        sx={{ color: 'red', cursor: 'pointer' }}
        onClick={() => {
          setShowModalConfirmDeleteOrder(true)
          setOrderCodeToDelete(orderCode)
        }}
        data-testid='delete-order-box-barcode-test-id'
      />
    ),
  }))

  const fulfillmentCenterOptions = useMemo(() => {
    const fcsOptions = fulfillmentCenters.map((fulfillmentCenter) => ({
      id: fulfillmentCenter.id,
      description: fulfillmentCenter.name,
    }))

    return fcsOptions
  }, [fulfillmentCenters])

  const renderContent = () => {
    if (restockingType > -1 && restockingSourceMovement > -1 && restockingDestinationMovement > -1) {
      if (
        badgeValidationSuccess && (
          restockingType === RestockingListTypesEnum.CHECK_IN_GROCERY
          || restockingType === RestockingListTypesEnum.CHECK_IN_FRESH
        )
      ) {
        return (
          <S.CheckinOrdersContainer>
            <TableMui
              columns={[
                { id: 'orderCode', label: 'Cód. do pedido' },
                { id: 'remove', label: 'Deletar' },
              ]}
              flatTable
              thinLine
              rows={orderCodeRows}
              enablePagination={false}
              emptyLabel='Nenhum pedido adicionado'
              maxHeight='30vh'
              minHeight='30vh'
              testId='table-order-code-test-id'
            />
            <S.CustomButton
              title='Criar lista e iniciar reposição'
              bgColor='green2'
              hoverColor='confirmHover'
              letterColor='white'
              data-testid='create-list-button-test'
              disabled={orderCodes.length === 0}
              onClick={() => setShowModalConfirmCreateList(true)}
            />
            <ScanCode
              onSubmit={onOrderBoxBarcodeSubmit}
              register={register}
              text='bipe o código da caixa do pedido'
              inputName='orderBoxBarcode'
              dataTestId='order-box-barcode-test-id'
              hasError={!!errors.orderBoxBarcode}
              errorMessage={errors.orderBoxBarcode?.message}
            />
          </S.CheckinOrdersContainer>
        )
      }

      if (!showModalBadgeValidation) {
        return (
          <>
            {transferTypes.includes(restockingType) && (
              <S.SelectContainer>
                <S.CustomSelect
                  formControlSx={{ ...fixInputSx, minWidth: '20rem' }}
                  name='fulfillmentCenterIdDestination'
                  inputProps={{
                    'data-testid': 'fc-destination-transfer-test-id',
                  }}
                  autoComplete='off'
                  id='fulfillmentCenterIdDestination'
                  label='Selecione um FC de destino'
                  variant='outlined'
                  options={fulfillmentCenterOptions}
                  optionNone={false}
                  type='text'
                  size='medium'
                  dataTestId='fc-destination-transfer-test-id'
                  value={destinationFc === -1 ? '' : destinationFc}
                  onChange={(e) => setDestinationFc(e.target.value as number)}
                  disabled={selectedRestockingType?.destination !== RestockingMovementTypesEnum.FREE}
                  error={!!fcDestinationError}
                  helperText={fcDestinationError}
                />
                <S.CustomTextField
                  name='shippingDate'
                  inputProps={{
                    'data-testid': 'expedition-date-test-id',
                    style: {
                      height: '1.95rem',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  autoComplete='off'
                  id='shippingDate'
                  label='Data de expedição'
                  variant='outlined'
                  type='date'
                  value={shippingDate}
                  onChange={(e) => setShippingDate(e.target.value as string)}
                />
              </S.SelectContainer>
            )}
            <FormProductsList
              idRestockingType={restockingType}
              setShedCode={setShedCode}
              setRestockingSubType={setRestockingSubType}
            />
            <TableMui
              columns={[
                { id: 'productCode', label: 'Cód. produto' },
                { id: 'productDescription', label: 'Descrição do produto' },
                { id: 'restockQuantity', label: 'Qtd. a repor em UN' },
                { id: 'address', label: 'End. origem', width: 220 },
                { id: 'quantity', label: 'Qtd. origem' },
                { id: 'expirationDate', label: 'Menor validade' },
                { id: 'minimumExpirationDate', label: 'Validade mínima' },
                { id: 'destinationAddress', label: 'End. destino', width: 220 },
              ]}
              emptyLabel='Nenhuma página gerada'
              rows={tableRows}
              enablePagination
              rowsPerPageLimit={10}
            />
            <FormGenerateLists
              idRestockingType={restockingType}
              productsList={productList}
              shedCode={shedCode}
              restockingSubType={restockingSubType}
              shippingDate={shippingDate}
              idFulfillmentCenterDestination={destinationFc}
              isValidFcDestination={isValidFcDestination}
            />
          </>
        )
      }
    }
    return null
  }

  const onErrorStartCheckinRestockList = () => {
    setShowModalToStartExecution(false)
    dispatch(actions.clearProperty('orderCodes'))
    reset({ orderBoxBarcode: '' })
  }

  return (
    <S.Wrapper>
      <S.SelectContainer>
        <S.CustomSelect
          formControlSx={{ ...fixInputSx, minWidth: '20rem' }}
          name='restockingType'
          inputProps={{
            'data-testid': 'restoking-type-test-id',
          }}
          autoComplete='off'
          id='restockingType'
          label='Selecione o tipo de reposição'
          variant='outlined'
          options={restockingListTypesOptions}
          optionNone={false}
          type='text'
          autoFocus
          onChange={handleChangeRestockingType}
          size='medium'
          dataTestId='restoking-type-test-id'
          searchBar
        />
        <S.CustomSelect
          formControlSx={{ ...fixInputSx, minWidth: '10rem' }}
          name='restockingSourceMovement'
          inputProps={{
            'data-testid': 'restoking-source-movement-test-id',
          }}
          autoComplete='off'
          id='restockingSourceMovement'
          label='End. Origem'
          variant='outlined'
          options={restockingMovementTypeOptions}
          optionNone={false}
          type='text'
          size='medium'
          dataTestId='restoking-source-movement-test-id'
          value={restockingSourceMovement === -1 ? '' : restockingSourceMovement}
          onChange={(e) => setRestockingSourceMovement(e.target.value as number)}
          disabled={selectedRestockingType?.source !== RestockingMovementTypesEnum.FREE}
        />
        <S.CustomSelect
          formControlSx={{ ...fixInputSx, minWidth: '10rem' }}
          name='restockingDestinationMovement'
          inputProps={{
            'data-testid': 'restoking-destination-movement-test-id',
          }}
          autoComplete='off'
          id='restockingDestinationMovement'
          label='End. Destino'
          variant='outlined'
          options={restockingMovementTypeOptions}
          optionNone={false}
          type='text'
          size='medium'
          dataTestId='restoking-destination-movement-test-id'
          value={restockingDestinationMovement === -1 ? '' : restockingDestinationMovement}
          onChange={(e) => setRestockingDestinationMovement(e.target.value as number)}
          disabled={selectedRestockingType?.destination !== RestockingMovementTypesEnum.FREE}
        />
      </S.SelectContainer>
      {renderContent()}
      <BadgeValidationModal
        show={showModalBadgeValidation}
        title='Insira o seu crachá'
        closedButton={false}
        dataTestId='badge-validation-modal-test-id'
        onClose={closeShowModalBadgeValidation}
        actionFunction={onBadgeSubmit}
        register={register}
        inputName='badge'
        errors={errors.badge}
        errorMessage={errors.badge?.message}
      />
      <ConfirmOrRejectModal
        show={showModalConfirmDeleteOrder}
        title='Deletar pedido'
        description={`Deseja realmente deletar o pedido: ${orderCodeToDelete}?`}
        onConfirm={deleteOrderCode}
        onReject={closeModalConfirmDeleteOrder}
        onClose={closeModalConfirmDeleteOrder}
        dataTestId='confirm-delete-order-code-modal-test-id'
      />
      <ConfirmOrRejectModal
        show={showModalConfirmCreateList}
        title='Criar lista de reposição'
        description='Deseja realmente concluir a bipagem dos pedidos e criar a lista de reposição?'
        onConfirm={generateCheckinRestockList}
        onReject={closeModalConfirmCreateList}
        onClose={closeModalConfirmCreateList}
        dataTestId='confirm-create-list-modal-test-id'
      />
      <StartExecutionModalCheckinList
        title={modalCheckinListGenerationError ? 'Erro ao tentar gerar lista' : 'Iniciar reposição'}
        show={showModalToStartExecution}
        errors={checkinListGenerationErrors}
        errorMode={modalCheckinListGenerationError}
        errorFn={removeErroredOrdersAndCloseModal}
        onError={onErrorStartCheckinRestockList}
        onClose={() => setShowModalToStartExecution(false)}
      />
    </S.Wrapper>
  )
}
